import {
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  runTransaction,
  serverTimestamp,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "./firebase-config";
import { getLevelFromExp } from "./utils/levels";

// ══════════════════════════════════════════════════════════════
// INSCRIPCIONES A EVENTOS Y NOTICIAS (agregado 11/10/2026)
//
// Cada evento o noticia puede mostrar un botón "Inscribirme". Se
// activa desde el panel de Admin con un campo booleano en el propio
// documento:
//   - Noticias/{id}.Inscripcion  (mayúscula, como el resto de Noticias)
//   - Eventos/{id}.inscripcion   (minúscula, como el resto de Eventos)
//
// Los inscriptos NO se guardan como un array dentro del documento,
// sino en una subcolección, un documento por usuario:
//   Eventos/{eventoId}/Inscriptos/{uid}
//   Noticias/{noticiaId}/Inscriptos/{uid}
//
// ¿Por qué subcolección y no un array?
// - Las reglas de Firestore pueden garantizar que cada usuario solo
//   cree o borre SU propio documento (id = uid). Con un array, el
//   usuario tendría que poder editar el evento entero.
// - Dos usuarios que se inscriben al mismo tiempo no se pisan.
// - Usar el uid como id hace imposible inscribirse dos veces.
//
// OJO: borrar un evento o noticia en Firestore NO borra su
// subcolección. Por eso el Admin llama a DeleteAllInscriptos()
// antes de eliminar.
// ══════════════════════════════════════════════════════════════

export type InscripcionColeccion = "Eventos" | "Noticias";

export interface Inscripto {
  Uid: string;
  Nombre: string;
  Email: string;
  Fecha: Date | null;
  /** XP que ya se le acreditó por este evento/noticia (null = todavía no). */
  PuntosOtorgados: number | null;
  PuntosOtorgadosEl: Date | null;
}

const inscriptosRef = (coleccion: InscripcionColeccion, itemId: string) =>
  collection(db, coleccion, itemId, "Inscriptos");

const inscriptoDocRef = (
  coleccion: InscripcionColeccion,
  itemId: string,
  uid: string,
) => doc(db, coleccion, itemId, "Inscriptos", uid);

/** Inscribe al usuario. El id del documento es su uid. */
export async function Inscribirse(
  coleccion: InscripcionColeccion,
  itemId: string,
  datos: { uid: string; nombre: string; email: string },
) {
  try {
    await setDoc(inscriptoDocRef(coleccion, itemId, datos.uid), {
      Uid: datos.uid,
      Nombre: datos.nombre,
      Email: datos.email,
      Fecha: serverTimestamp(),
    });
  } catch (error) {
    console.error(error);
    throw error;
  }
}

/** Da de baja una inscripción (la usa el propio usuario o el admin). */
export async function CancelarInscripcion(
  coleccion: InscripcionColeccion,
  itemId: string,
  uid: string,
) {
  try {
    await deleteDoc(inscriptoDocRef(coleccion, itemId, uid));
  } catch (error) {
    console.error(error);
    throw error;
  }
}

/**
 * Escucha en tiempo real si el usuario está inscripto.
 * Devuelve la función para desuscribirse (para usar en un useEffect).
 */
export function SubscribeMiInscripcion(
  coleccion: InscripcionColeccion,
  itemId: string,
  uid: string,
  callback: (inscripto: boolean) => void,
) {
  return onSnapshot(
    inscriptoDocRef(coleccion, itemId, uid),
    (snapshot) => callback(snapshot.exists()),
    (error) => {
      console.error("Error al leer la inscripción:", error);
      callback(false);
    },
  );
}

const toDate = (value: unknown): Date | null => {
  const v = value as { toDate?: () => Date; seconds?: number } | null;
  if (v && typeof v.toDate === "function") return v.toDate();
  if (v && typeof v.seconds === "number") return new Date(v.seconds * 1000);
  return null;
};

/** Lista de inscriptos, del primero al último en anotarse (solo admin). */
export async function GetInscriptos(
  coleccion: InscripcionColeccion,
  itemId: string,
): Promise<Inscripto[]> {
  const snapshot = await getDocs(inscriptosRef(coleccion, itemId));
  return snapshot.docs
    .map((entry) => {
      const d = entry.data();
      return {
        Uid: typeof d.Uid === "string" ? d.Uid : entry.id,
        Nombre: typeof d.Nombre === "string" ? d.Nombre : "",
        Email: typeof d.Email === "string" ? d.Email : "",
        Fecha: toDate(d.Fecha),
        PuntosOtorgados:
          typeof d.PuntosOtorgados === "number" ? d.PuntosOtorgados : null,
        PuntosOtorgadosEl: toDate(d.PuntosOtorgadosEl),
      };
    })
    .sort(
      (a, b) => (a.Fecha?.getTime() ?? 0) - (b.Fecha?.getTime() ?? 0),
    );
}

/** Borra todos los inscriptos de un evento/noticia (antes de eliminarlo). */
export async function DeleteAllInscriptos(
  coleccion: InscripcionColeccion,
  itemId: string,
) {
  const snapshot = await getDocs(inscriptosRef(coleccion, itemId));
  // Un batch admite hasta 500 operaciones.
  for (let i = 0; i < snapshot.docs.length; i += 450) {
    const batch = writeBatch(db);
    snapshot.docs.slice(i, i + 450).forEach((entry) => batch.delete(entry.ref));
    await batch.commit();
  }
}

// ══════════════════════════════════════════════════════════════
// OTORGAR LA XP DEL EVENTO A UN INSCRIPTO (agregado 04/11/2026)
//
// Lo usa el botón "Dar XP" de la lista de inscriptos del Admin.
// En una sola transacción:
//   1. Suma la XP a Usuarios/{uid}.CurrentExp y recalcula el Nivel.
//   2. Le agrega una notificación: "Recibiste X puntos por participar
//      de {nombre}".
//   3. Marca la inscripción con PuntosOtorgados / PuntosOtorgadosEl,
//      para que el botón pase a "Otorgado" y no se pueda dar dos veces.
//   4. Guarda "Eventos/{id}" en Usuarios/{uid}.XpOtorgadaPor: así, si
//      el usuario se da de baja y se vuelve a inscribir, tampoco se le
//      puede acreditar de nuevo.
//
// Con el importador de Excel no hay conflicto: esta XP no toca
// XpAcademica, así que el importador la conserva como XP "extra".
// ══════════════════════════════════════════════════════════════

export class XpYaOtorgadaError extends Error {
  constructor() {
    super("Este usuario ya recibió la XP de este evento.");
    this.name = "XpYaOtorgadaError";
  }
}

export class UsuarioNoEncontradoError extends Error {
  constructor() {
    super("El usuario inscripto ya no existe en Usuarios.");
    this.name = "UsuarioNoEncontradoError";
  }
}

export async function OtorgarPuntosInscripto(params: {
  coleccion: InscripcionColeccion;
  itemId: string;
  titulo: string;
  uid: string;
  puntos: number;
}): Promise<{ nuevaExp: number; nuevoNivel: number }> {
  const { coleccion, itemId, titulo, uid, puntos } = params;
  if (!Number.isFinite(puntos) || puntos <= 0) {
    throw new Error("La XP a otorgar tiene que ser mayor a 0.");
  }

  const clave = `${coleccion}/${itemId}`;
  const inscriptoRef = inscriptoDocRef(coleccion, itemId, uid);
  const userRef = doc(db, "Usuarios", uid);

  return runTransaction(db, async (tx) => {
    const [inscriptoSnap, userSnap] = await Promise.all([
      tx.get(inscriptoRef),
      tx.get(userRef),
    ]);

    if (!userSnap.exists()) throw new UsuarioNoEncontradoError();

    const inscripto = inscriptoSnap.data() ?? {};
    const user = userSnap.data();
    const yaOtorgadas: unknown[] = Array.isArray(user.XpOtorgadaPor)
      ? user.XpOtorgadaPor
      : [];

    if (
      typeof inscripto.PuntosOtorgados === "number" ||
      yaOtorgadas.includes(clave)
    ) {
      throw new XpYaOtorgadaError();
    }

    const expActual =
      typeof user.CurrentExp === "number" && Number.isFinite(user.CurrentExp)
        ? user.CurrentExp
        : 0;
    const nuevaExp = expActual + puntos;
    const nuevoNivel = getLevelFromExp(nuevaExp);

    tx.update(userRef, {
      CurrentExp: nuevaExp,
      Nivel: nuevoNivel,
      XpOtorgadaPor: arrayUnion(clave),
      // serverTimestamp() no se puede usar dentro de un array: va Date,
      // igual que en el importador de XP.
      Notifications: arrayUnion({
        Title: "¡Sumaste XP!",
        Description: `Recibiste ${puntos} puntos por participar de ${titulo}.`,
        Checked: false,
        Icon: "🎉",
        Date: new Date(),
      }),
    });

    if (inscriptoSnap.exists()) {
      tx.update(inscriptoRef, {
        PuntosOtorgados: puntos,
        PuntosOtorgadosEl: serverTimestamp(),
      });
    }

    return { nuevaExp, nuevoNivel };
  });
}
